import { Box } from '@inflearn/ds-react';
import { useTranslation } from 'next-i18next';

import { useLoadableFeature } from '@hackler/react-sdk';
import { useLang } from '@inflearn/i18n-tools';
import { USERS_URL } from '../../../constants/urls';
import useCheckMyProfile from '../../../hooks/useCheckMyProfile';
import { useCurrentUrl } from '../../../hooks/useCurrentUrl';
import useQueryParams from '../../../hooks/useQueryParams';
import { CLIP } from '../../../lib/hackle/featureKeys';
import UserNavigationEl from './UserNavigationEl';

type Props = {
  hasRoadmap?: boolean;
  hasCourse?: boolean;
  hasReview?: boolean;
  hasBlog?: boolean;
  hasPost?: boolean;
  hasClip?: boolean;
  userSlug?: string;
};

export function UserNavigation({
  hasReview,
  hasRoadmap,
  hasCourse,
  hasBlog,
  hasPost,
  hasClip,
  userSlug,
}: Props) {
  const { t } = useTranslation();
  const { isKo } = useLang();
  const { isOn } = useLoadableFeature(CLIP);
  const { currentUrlWithoutQueryString } = useCurrentUrl();

  const { id } = useQueryParams();
  const { isMe } = useCheckMyProfile();

  const NAVIGATION_LINKS = [
    {
      label: t('홈'),
      path: '',
      getHref: (userId: string, slug?: string) => USERS_URL.HOME({ userId, slug }),
      enabled: true,
    },
    {
      label: t('강의'),
      path: 'courses',
      getHref: (userId: string) => USERS_URL.COURSES(userId),
      enabled: hasCourse,
    },
    {
      label: '클립',
      path: 'clips',
      getHref: (userId: string) => USERS_URL.CLIPS(userId),
      enabled: isOn && (isMe || hasClip) && isKo,
    },
    {
      label: t('로드맵'),
      path: 'roadmaps',
      getHref: (userId: string) => USERS_URL.ROADMAPS(userId),
      enabled: (isMe || hasRoadmap) && isKo,
    },
    {
      label: t('수강평'),
      path: 'reviews',
      getHref: (userId: string) => USERS_URL.COURSE_REVIEWS(userId),
      enabled: hasReview,
    },
    {
      label: t('게시글'),
      path: 'posts',
      getHref: (userId: string) => USERS_URL.POSTS(userId),
      enabled: isMe || hasPost,
    },
    {
      label: t('블로그'),
      path: 'blogs',
      getHref: (userId: string) => USERS_URL.BLOGS(userId),
      enabled: (isMe || hasBlog) && isKo,
    },
  ];

  return (
    <nav>
      <Box
        component="ul"
        py={16}
        css={{
          padding: 0,
          display: 'flex',
          flexWrap: 'nowrap',
          overflowX: 'auto',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },

          li: {
            padding: '14px 16px',
            whiteSpace: 'nowrap',
          },
        }}
      >
        {NAVIGATION_LINKS.filter(({ enabled }) => enabled).map(({ label, getHref }) => {
          const href = getHref(String(id), userSlug);
          const isActive = currentUrlWithoutQueryString === href;

          return <UserNavigationEl key={label} isActive={isActive} href={href} label={label} />;
        })}
      </Box>
    </nav>
  );
}
